import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { PagingSize } from './paging-size.model';
import { AriaLabel } from '@shared/constants/aria-label';

@Component({
  selector: 'app-paging',
  templateUrl: './paging.component.html',
  styleUrls: ['./paging.component.scss']
})
export class PagingComponent implements OnChanges {
  @Input() currentPage: number = 1;
  @Input() pageSizes: number[];
  @Input() invertStyle: boolean = false;
  @Input() showFilter: boolean = false;
  @Input() filterRecordType: string = 'records';
  @Input() isBottom: boolean = false;
  @Input() customHeader: string;
  @Input() hidePagingHeader = false;
  @Input() showPagingTop: boolean = true;

  filterPanelOpen: boolean = false;
  @Output() showFilterChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() currentPageChanged: EventEmitter<PagingSize> = new EventEmitter<PagingSize>();

  selectedPageSize: number;

  private _totalCount: number;
  rightChevron: string = AriaLabel.RightChevron;
  leftChevron: string = AriaLabel.LeftChevron;
  @Input() set totalCount(value: number) {
    this._totalCount = value;
  }

  get totalCount(): number {
    return this._totalCount;
  }

  get pageStart(): number {
    return this._totalCount == 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get pageEnd(): number {
    const currentLastElementPosition = this.currentPage * this.pageSize;
    return Math.min(currentLastElementPosition, this.totalCount);
  }

  private _pageSize: number;
  @Input() set pageSize(value: number) {
    this._pageSize = value;
    this.selectedPageSize = value; //set the drop down value to match if set externally
  }
  get pageSize(): number {
    return this._pageSize;
  }

  displayPages: number[] = [];
  maxPage: number;

  ngOnChanges(changes: SimpleChanges): void {
    this.setup();
  }

  setup() {
    this.maxPage = Math.ceil(this.totalCount / this.pageSize);

    //If the current page is higher than the max, set it to the max
    if (this.currentPage > this.maxPage && this.currentPage > 1) {
      this.currentPage = this.maxPage;
    }

    let minDisplayPage = 0;
    const totalOfDisplayPages = 6;

    // If we are on a page that is somewhere in the middle (e.g. Page 4 of 8)
    if (this.currentPage > 1) {
      minDisplayPage = this.currentPage - (totalOfDisplayPages - 1);
    }

    // If we are actually on the last page.
    if (this.currentPage === this.maxPage) {
      minDisplayPage = this.currentPage - (totalOfDisplayPages - 1);
    }

    // If the minimum page has been worked out to be less than 1, then set it to 1.
    if (minDisplayPage < 1) {
      minDisplayPage = 1;
    }

    this.displayPages = [];
    const pages: number[] = [];
    // Loop through and add pages up to either the maximum, or until we've got 3 pages.
    for (let i = minDisplayPage; i <= this.maxPage && i <= minDisplayPage + (totalOfDisplayPages - 1); i++) {
      pages.push(i);
    }
    this.displayPages = [...pages];

    if (this.currentPage === 0) {
      this.page(1);
    }
  }

  /**
   * Change the current page from html
   * @param pageNumber
   */
  uxPage(pageNumber: number) {
    if (this.currentPage != pageNumber) {
      this.page(pageNumber);
    }
  }

  page(pageNumber: number) {
    if (
      pageNumber > 0 && // Not trying to navigate to page 0.
      pageNumber <= this.maxPage // Not trying to go past the last page.
    ) {
      // Actually moving to a different page (e.g. Not trying to load the same page twice. )
      this.currentPage = pageNumber;
      this.currentPageChanged.emit(new PagingSize(this.currentPage, this.pageSize));
      this.setup();
    }
  }

  setPageSize() {
    this.currentPage = 0;
    this.pageSize = this.selectedPageSize;
    this.page(1);
  }

  /**
   * Callback when the filter panel is opened or closed.
   * @param display
   */
  displayFilter(display: boolean) {
    this.filterPanelOpen = display;
    this.showFilterChanged.next(display);
  }
}
